import { interpolate } from './interpolation';
import type { CmsVariables } from './interpolation';

/**
 * Token interpolation for author HTML, as produced by Puck's richtext field.
 *
 * `interpolate()` is a flat regex over a string and stays exactly as it is: four other blocks
 * (Heading, Product Carousel's heading, Image's caption, Button's label) still store plain strings
 * and must keep byte-for-byte identical behaviour. This module adds a richtext-aware path beside it
 * rather than changing it.
 *
 * ## Why a separate path is needed at all
 *
 * `TOKEN_PATTERN` cannot match across a tag boundary, and Tiptap preserves fragmentation verbatim.
 * Measured against the real Tiptap 3.28 engine: bolding one word mid-token stores
 * `<p>Welcome to {{na<strong>me}}</strong></p>`, and a flat replace leaves a literal `{{name}}` on
 * the published page with no error anywhere. Six of nine fragmentation shapes failed that way.
 *
 * ## The trilemma, and why this is the chosen corner
 *
 * Correct substitution, preserved formatting, flat-string regex -- pick two.
 *
 * Collapsing a block to a single unformatted text run substitutes correctly (8 of 9 cases) but
 * destroys the author's other formatting in that block: `<strong>Sale!</strong> Visit {{name}}`
 * comes back as plain `Sale! Visit Green Leaf`. That trades a visible bug for silent data loss,
 * which is strictly worse, so it is rejected.
 *
 * Interpolating each text run independently -- what this module does -- preserves every mark and is
 * never worse than today's behaviour, but cannot substitute a token whose characters are split
 * across runs. That residual case is an authoring mistake, so it is surfaced where it is made:
 * `findFragmentedTokens` powers an editor warning rather than a silent live-page failure.
 *
 * A dedicated Tiptap atom node would make tokens unsplittable by construction and remove the
 * residual case entirely. That is the deliberate path to zero literal tokens, and it is
 * meaningfully more work (a custom Puck field wrapping a custom Tiptap extension), so it is
 * recorded here rather than left to be rediscovered.
 */

/**
 * Matches a text run between tags. Deliberately does not attempt to understand HTML: it only needs
 * to find the spans a token could legally live inside, and a token can never contain `<` or `>`
 * (`TOKEN_PATTERN` accepts `\w` only), so a run boundary is always a token boundary too.
 */
const TEXT_RUN_PATTERN = /(^|>)([^<>]+)(?=<|$)/g;

/** The same token shape `interpolation.ts` accepts, used here only for detection. */
const TOKEN_DETECT_PATTERN = /{{\s*(\w+)\s*}}/g;

/**
 * Replaces `{{token}}` placeholders inside author HTML, run by run.
 *
 * Markup is never inspected or rewritten -- only the text between tags is passed to `interpolate`,
 * so every tag, attribute and mark survives untouched. A document containing no token is returned
 * unchanged: each run is handed to `interpolate`, which returns its input when nothing matches, and
 * the runs are reassembled in place.
 *
 * MUST run BEFORE `sanitizeCmsHtml`, never after. A dispensary name and address are operator-edited
 * data, not trusted input: substituting after sanitisation puts an unsanitised value into the final
 * HTML, which was measured to leave a live `<img src=x onerror=...>` in the output and to inject an
 * `onmouseover` handler when a token sat inside an `href`. Interpolating first means the substituted
 * value is sanitised along with everything else.
 */
export function interpolateHtml(html: string | undefined, variables: CmsVariables | undefined): string {
  if (!html) {
    return '';
  }
  if (!variables) {
    return html;
  }
  return html.replace(
    TEXT_RUN_PATTERN,
    (_match, prefix: string, run: string) => `${prefix}${interpolate(run, variables)}`
  );
}

/**
 * Returns the token keys that appear in the document's text but are split across runs, so no single
 * run contains a whole one.
 *
 * These are exactly the tokens `interpolateHtml` cannot substitute, and the reason the editor warns
 * rather than letting a literal `{{name}}` reach a published page.
 *
 * ## What it must not get wrong
 *
 * A token-like sequence split across a BLOCK boundary is two separate pieces of text, not one
 * fragmented token: `<p>{{na</p><p>me}}</p>` is an author typing braces in two paragraphs, and
 * joining across the boundary would invent a token neither paragraph contains. So runs are joined
 * per block, never across blocks.
 *
 * A block whose runs each contain their own complete token can still hide a fragmented one:
 * `<p>{{name}} and {{ad<strong>dress}}</strong></p>`. Detection therefore compares the tokens in
 * the joined text against the tokens found in the individual runs, rather than asking whether any
 * run had a token at all.
 */
export function findFragmentedTokens(html: string | undefined): string[] {
  if (!html) {
    return [];
  }

  const fragmented = new Set<string>();

  splitIntoBlocks(html).forEach((block) => {
    const runs = textRunsOf(block);
    if (runs.length < 2) {
      // A single run cannot hide a split token: whatever `interpolate` sees is what is there.
      return;
    }
    const joined = runs.join('');
    const joinedTokens = tokenKeysOf(joined);
    if (joinedTokens.length === 0) {
      return;
    }
    // Count per key rather than test presence: a key can legitimately appear once whole and once
    // split in the same block, and the split occurrence must still be reported.
    const runTokenCounts = countTokenKeys(runs.join('\u0000'));
    const joinedCounts = countTokenKeys(joined);

    Object.keys(joinedCounts).forEach((key) => {
      if ((joinedCounts[key] ?? 0) > (runTokenCounts[key] ?? 0)) {
        fragmented.add(key);
      }
    });
  });

  return [...fragmented];
}

/**
 * Splits HTML at block boundaries, so runs are never joined across them.
 *
 * Uses the block-level tags Puck's richtext can produce (`p`, `h1`-`h6`, `blockquote`, `li`, and
 * their containers). The point of splitting is to avoid a FALSE POSITIVE: text in two different
 * blocks is never one author sentence, so joining `<p>{{na</p><p>me}}</p>` would invent a token
 * the author never wrote.
 *
 * `br` is deliberately NOT in this list, even though it renders a visible break. A `br` sits
 * INSIDE one block, so `{{na<br>me}}` is one continuous author string that happens to wrap --
 * and `interpolateHtml` cannot substitute across it either, because `TEXT_RUN_PATTERN` stops at
 * the tag exactly as it does for `<strong>`. Treating `br` as a boundary therefore suppressed a
 * real fragmentation instead of a phantom one: the author got no warning and shipped a literal
 * `{{name}}` to a live page. Leaving it out lets `textRunsOf` yield the two runs and the
 * join-and-compare below catch it, identically to a bold/em/underline split.
 */
function splitIntoBlocks(html: string): string[] {
  return html.split(/<\/?(?:p|h[1-6]|blockquote|li|ul|ol|div)\b[^>]*>/i);
}

/** The text runs of a fragment, in order, with markup removed but boundaries preserved. */
function textRunsOf(fragment: string): string[] {
  const runs: string[] = [];
  let match = TEXT_RUN_PATTERN.exec(fragment);
  while (match !== null) {
    runs.push(match[2]);
    match = TEXT_RUN_PATTERN.exec(fragment);
  }
  TEXT_RUN_PATTERN.lastIndex = 0;
  return runs;
}

function tokenKeysOf(text: string): string[] {
  const keys: string[] = [];
  let match = TOKEN_DETECT_PATTERN.exec(text);
  while (match !== null) {
    keys.push(match[1]);
    match = TOKEN_DETECT_PATTERN.exec(text);
  }
  TOKEN_DETECT_PATTERN.lastIndex = 0;
  return keys;
}

function countTokenKeys(text: string): Record<string, number> {
  return tokenKeysOf(text).reduce<Record<string, number>>((acc, key) => {
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
}
